import { bulkCreateTable } from "../../../ApiCalls/mantisFlowsBulks";

/**
 * Process and validate items for bulk upload
 * @param {Array} items - Array of items to process
 * @param {Object} config - Configuration object with validation rules
 * @returns {Object} Object containing valid and invalid items
 */
export const processBulkItems = (items, config) => {
  const validItems = [];
  const invalidItems = [];
  const errors = [];

  items.forEach((item, index) => {
    const validation = config.validateItem(item);
    
    if (validation.isValid) {
      validItems.push(item);
    } else {
      invalidItems.push({
        item,
        index,
        errors: validation.errors
      });
      errors.push(...validation.errors.map(error => `Item ${index + 1}: ${error}`));
    }
  });

  return {
    validItems,
    invalidItems,
    errors,
    hasErrors: errors.length > 0
  };
};

/**
 * Upload items in bulk
 * @param {string} clientId - Client ID
 * @param {Array} items - Array of items to upload
 * @param {Object} config - Configuration object
 * @param {Object} options - Additional options for the upload
 * @returns {Promise<Object>} Result of the bulk upload operation
 */
export const uploadBulkItems = async (clientId, items, config, options = {}) => {
  try {
    // Validate clientId
    if (!clientId) {
      throw new Error('Client ID is missing');
    }

    // Process and validate items
    const { validItems, invalidItems, errors, hasErrors } = processBulkItems(items, config);

    // If there are errors and the user hasn't explicitly chosen to proceed with valid items only
    if (hasErrors && !options.proceedWithValidItemsOnly) {
      return {
        success: false,
        errors,
        invalidItems,
        validItems,
        canProceedWithValidItems: validItems.length > 0
      };
    }

    // If there are no valid items to upload
    if (validItems.length === 0) {
      return {
        success: false,
        errors: errors.length > 0 ? errors : ["No valid items to upload"],
        invalidItems,
        validItems: []
      };
    }

    // Upload valid items
    const response = await bulkCreateTable(
      clientId,
      config.tableName,
      validItems,
      options
    );

    return {
      success: true,
      data: response,
      validItems,
      invalidItems,
      errors: hasErrors ? errors : []
    };
  } catch (error) {
    console.error("Error in uploadBulkItems:", error);
    return {
      success: false,
      errors: [error.message || "An error occurred during bulk upload"],
      validItems: [],
      invalidItems: items.map((item, index) => ({
        item,
        index,
        errors: ["Failed to upload"]
      }))
    };
  }
};

/**
 * Format bulk upload results for display
 * @param {Object} result - Result from uploadBulkItems
 * @returns {Object} Formatted result object
 */
export const formatBulkUploadResult = (result) => {
  return {
    success: result.success,
    totalItems: result.validItems.length + result.invalidItems.length,
    successfulItems: result.validItems.length,
    failedItems: result.invalidItems.length,
    errors: result.errors || [],
    canProceedWithValidItems: result.canProceedWithValidItems || false,
    details: result.invalidItems.map(item => ({
      index: (item.index !== undefined ? item.index : 0) + 1,
      errors: item.errors
    }))
  };
}; 